import React, { useMemo } from 'react';
import { Dropdown } from '@packages/ui/composites/Dropdown';
import type { InternalActionDef } from '../schema/types';
import { evaluateIsActionable } from '../../utils/evaluateIsActionable';
import { TABLE_IDS } from '../semantic-contract';
import type { SubRowContext } from './ActionRenderer';

export interface VisibleRowAction {
  action: InternalActionDef;
  state: ReturnType<typeof evaluateIsActionable>;
}

export function useVisibleRowActions(
  actions: ReadonlyArray<InternalActionDef>,
  row: Record<string, unknown>,
  namespace: string,
  checkPermission?: (permission: { action: string; resource: string }) => boolean,
): VisibleRowAction[] {
  return useMemo(
    () => actions
      .map((action) => ({ action, state: evaluateIsActionable(namespace, action, row, checkPermission) }))
      .filter((entry) => entry.state.visible),
    [actions, row, namespace, checkPermission],
  );
}

interface RowActionMenuItemsProps {
  entries: ReadonlyArray<VisibleRowAction>;
  rowIndex: number;
  onExecuteAction: (action: InternalActionDef) => void;
  emptyLabel?: string;
}

export function RowActionMenuItems({
  entries,
  rowIndex,
  onExecuteAction,
  emptyLabel,
}: RowActionMenuItemsProps) {
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {entries.length === 0 && emptyLabel ? (
        <li><Dropdown.Item disabled>{emptyLabel}</Dropdown.Item></li>
      ) : (
        entries.map(({ action, state }) => (
          <li key={action.id} id={action.semanticId ?? undefined}>
            <Dropdown.Item
              onSelect={() => state.enabled && onExecuteAction(action)}
              disabled={!state.enabled}
              semanticId={TABLE_IDS.actionItem(action.id, rowIndex)}
              color={action.variant === 'danger' ? 'danger' : undefined}
              icon={action.icon ? <span className='material-symbols-outlined'>{action.icon}</span> : undefined}
              title={!state.enabled ? state.reason : undefined}
            >
              {action.label}
            </Dropdown.Item>
          </li>
        ))
      )}
    </ul>
  );
}
